import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  CopyButton,
  Divider,
  Drawer,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconPlus, IconX } from '@tabler/icons-react';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { api, AppUser, Role, ROLE_NAMES, UserRole } from '../api';
import { hasRole, useSession } from '../session';

const ALL_ROLES: Role[] = ['owner', 'finance', 'manager', 'dept_head', 'admin', 'viewer'];
const ROLE_HINTS: Record<Role, string> = {
  owner: 'всё, включая ФОТ и пользователей',
  finance: 'все операции, включая ФОТ',
  manager: 'своё ООО, без ФОТ',
  dept_head: 'своё подразделение, без ФОТ',
  admin: 'справочники, пользователи, источники; операции не видит',
  viewer: 'только дашборд',
};

/** Пароль для передачи сотруднику: без похожих символов (0/O, 1/l), при входе он его сменит. */
function generatePassword() {
  const abc = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  const s = [...bytes].map((b) => abc[b % abc.length]).join('');
  return `${s.slice(0, 4)}-${s.slice(4, 8)}-${s.slice(8)}`;
}

const roleLabel = (r: UserRole) => [ROLE_NAMES[r.role], r.department, r.legal_entity].filter(Boolean).join(' · ');

function Status({ u }: { u: AppUser }) {
  if (!u.is_active) return <Badge color="gray" variant="light">Выключен</Badge>;
  if (u.must_change_password) return <Badge color="orange" variant="light">Ждёт смены пароля</Badge>;
  return <Badge color="brand" variant="light">Активен</Badge>;
}

export function UsersPage() {
  const mobile = useMediaQuery('(max-width: 48em)');
  const [users, setUsers] = useState<AppUser[] | null>(null);
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async () => {
    const r = await api.get<{ users: AppUser[] }>('/users');
    setUsers(r.users);
  }, []);
  useEffect(() => {
    load().catch((e) => notifications.show({ color: 'red', message: e.message }));
  }, [load]);

  const replace = (u: AppUser) => setUsers((xs) => (xs ?? []).some((x) => x.id === u.id) ? (xs ?? []).map((x) => (x.id === u.id ? u : x)) : [...(xs ?? []), u]);
  const current = users?.find((u) => u.id === open) ?? null;

  return (
    <Box p={{ base: 'md', sm: 'xl' }} maw={1100}>
      <Group justify="space-between" mb="lg" wrap="wrap" gap="sm">
        <Title order={2}>Пользователи</Title>
        <Button leftSection={<IconPlus size={18} />} onClick={() => { setIssued(null); setOpen('new'); }}>
          Добавить пользователя
        </Button>
      </Group>

      <Box className="mis-card" style={{ overflow: 'hidden' }}>
        {users === null ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : mobile ? (
          <Stack gap={0}>
            {users.map((u) => (
              <Box key={u.id} px="md" py="sm" className="mis-row" style={{ borderTop: '1px solid var(--mis-border)' }} onClick={() => { setIssued(null); setOpen(u.id); }}>
                <Group justify="space-between" wrap="nowrap">
                  <Text fw={500} truncate c={u.is_active ? undefined : 'dimmed'}>{u.full_name}</Text>
                  <Status u={u} />
                </Group>
                <Text size="xs" c="dimmed" truncate>
                  {u.roles.map(roleLabel).join('; ') || 'нет ролей'}
                </Text>
              </Box>
            ))}
          </Stack>
        ) : (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Сотрудник</Table.Th>
                <Table.Th>Роли</Table.Th>
                <Table.Th>Состояние</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((u) => (
                <Table.Tr key={u.id} className="mis-row" onClick={() => { setIssued(null); setOpen(u.id); }}>
                  <Table.Td>
                    <Text fw={500} c={u.is_active ? undefined : 'dimmed'}>
                      {u.full_name} {u.is_self && <Text span size="xs" c="dimmed">(вы)</Text>}
                    </Text>
                    <Text size="xs" c="dimmed">{u.email}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      {u.roles.length ? u.roles.map((r) => <Badge key={r.id} variant="default" tt="none" fw={500}>{roleLabel(r)}</Badge>) : <Text size="sm" c="orange.8">нет ролей</Text>}
                    </Group>
                  </Table.Td>
                  <Table.Td><Status u={u} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      <Drawer
        opened={open !== null}
        onClose={() => setOpen(null)}
        position={mobile ? 'bottom' : 'right'}
        size={mobile ? '92%' : 460}
        title={<Text fw={600}>{open === 'new' ? 'Новый пользователь' : current?.full_name}</Text>}
      >
        {issued && <IssuedPassword {...issued} />}
        {open === 'new' && (
          <CreateUser
            onCreated={(u, password) => {
              replace(u);
              setIssued({ email: u.email, password });
              setOpen(u.id);
            }}
          />
        )}
        {current && <EditUser key={current.id} user={current} onChange={replace} onPassword={(password) => setIssued({ email: current.email, password })} />}
      </Drawer>
    </Box>
  );
}

function IssuedPassword({ email, password }: { email: string; password: string }) {
  return (
    <Alert color="brand" variant="light" mb="md" title="Пароль для входа">
      <Text size="sm" mb={6}>
        Передайте сотруднику лично. Больше он нигде не показывается; при входе сотрудник сменит его на свой.
      </Text>
      <Group gap="xs" wrap="nowrap">
        <Text ff="monospace" fz="lg" fw={600} data-testid="issued-password">{password}</Text>
        <CopyButton value={`${email}\n${password}`}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Скопировано' : 'Скопировать e-mail и пароль'}>
              <ActionIcon variant="subtle" onClick={copy} aria-label="Скопировать пароль">
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Alert>
  );
}

function CreateUser({ onCreated }: { onCreated: (u: AppUser, password: string) => void }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const password = generatePassword();
    try {
      onCreated(await api.post<AppUser>('/users', { full_name: fullName, email, password }), password);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="md">
        {error && <Alert color="red" variant="light" role="alert">{error}</Alert>}
        <TextInput label="Фамилия и имя" required value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} />
        <TextInput label="E-mail" type="email" required value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        <Text size="xs" c="dimmed">
          Пароль для первого входа МИС придумает сама и покажет один раз. Роли — на следующем шаге.
        </Text>
        <Button type="submit" loading={busy}>Создать</Button>
      </Stack>
    </form>
  );
}

function EditUser({ user, onChange, onPassword }: { user: AppUser; onChange: (u: AppUser) => void; onPassword: (p: string) => void }) {
  const { me, catalog } = useSession();
  const isOwner = hasRole(me, 'owner');
  const [fullName, setFullName] = useState(user.full_name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<Role | null>(null);
  const [le, setLe] = useState<string | null>(null);
  const [dep, setDep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (what: string, fn: () => Promise<AppUser | void>) => {
    setBusy(what);
    setError(null);
    try {
      const u = await fn();
      if (u) onChange(u);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const grantable = ALL_ROLES.filter((r) => isOwner || (r !== 'owner' && r !== 'finance'));
  const canGrant = user.manageable && (isOwner || !user.is_self);
  const dirty = fullName !== user.full_name || email !== user.email;

  if (!user.manageable) {
    return (
      <Stack gap="sm">
        <Text size="sm" c="dimmed">{user.email}</Text>
        <Group gap={6}>{user.roles.map((r) => <Badge key={r.id} variant="default" tt="none" fw={500}>{roleLabel(r)}</Badge>)}</Group>
        <Alert color="gray" variant="light">Собственника и финансиста меняет только собственник.</Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {error && <Alert color="red" variant="light" role="alert">{error}</Alert>}
      <form onSubmit={(e) => { e.preventDefault(); void run('save', () => api.patch<AppUser>(`/users/${user.id}`, { full_name: fullName, email })); }}>
        <Stack gap="sm">
          <TextInput label="Фамилия и имя" value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} />
          <TextInput label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
          {dirty && <Button type="submit" loading={busy === 'save'} variant="light">Сохранить</Button>}
        </Stack>
      </form>

      <Divider label="Роли" labelPosition="left" />
      <Stack gap={6}>
        {user.roles.length === 0 && <Text size="sm" c="orange.8">Ролей нет — сотрудник войдёт, но ничего не увидит.</Text>}
        {user.roles.map((r) => (
          <Group key={r.id} justify="space-between" wrap="nowrap" className="mis-card" px="sm" py={6} style={{ borderRadius: 12 }}>
            <div>
              <Text size="sm" fw={500}>{roleLabel(r)}</Text>
              <Text size="xs" c="dimmed">{ROLE_HINTS[r.role]}</Text>
            </div>
            {canGrant && (isOwner || (r.role !== 'owner' && r.role !== 'finance')) && (
              <ActionIcon variant="subtle" color="red" aria-label={`Снять роль ${roleLabel(r)}`} loading={busy === r.id}
                onClick={() => void run(r.id, () => api.del<AppUser>(`/users/${user.id}/roles/${r.id}`))}>
                <IconX size={16} />
              </ActionIcon>
            )}
          </Group>
        ))}
      </Stack>
      {canGrant ? (
        <Stack gap="xs">
          <Select label="Добавить роль" placeholder="Роль" data={grantable.map((r) => ({ value: r, label: `${ROLE_NAMES[r]} — ${ROLE_HINTS[r]}` }))}
            value={role} onChange={(v) => { setRole(v as Role | null); setDep(null); setLe(null); }} />
          {role === 'dept_head' && (
            <Select label="Подразделение" required data={(catalog?.departments ?? []).map((d) => ({ value: d.id, label: d.name }))} value={dep} onChange={setDep} />
          )}
          {(role === 'manager' || role === 'dept_head') && (
            <Select label="ООО" placeholder="Оба ООО" clearable data={(catalog?.legalEntities ?? []).map((l) => ({ value: l.id, label: l.name }))} value={le} onChange={setLe} />
          )}
          <Button variant="light" disabled={!role || (role === 'dept_head' && !dep)} loading={busy === 'role'}
            onClick={() => void run('role', async () => {
              const u = await api.post<AppUser>(`/users/${user.id}/roles`, { role, legal_entity_id: le, department_id: dep });
              setRole(null);
              return u;
            })}>
            Выдать роль
          </Button>
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">Свои роли меняет собственник.</Text>
      )}

      <Divider label="Доступ" labelPosition="left" />
      {!user.is_self && (
        <Button variant="default" loading={busy === 'password'}
          onClick={() => void run('password', async () => {
            const password = generatePassword();
            await api.post(`/users/${user.id}/password`, { password });
            onPassword(password);
            return { ...user, must_change_password: true };
          })}>
          Выдать новый пароль
        </Button>
      )}
      {!user.is_self && <Switch label="Может входить" checked={user.is_active} disabled={busy === 'active'}
        description={user.is_active ? 'Выключенный сотрудник сразу теряет доступ; записи и журнал остаются' : 'Вход закрыт'}
        onChange={(e) => { const v = e.currentTarget.checked; void run('active', () => api.patch<AppUser>(`/users/${user.id}`, { is_active: v })); }} />}
    </Stack>
  );
}
